import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Eye,
  RefreshCw,
  Search,
  XCircle,
} from 'lucide-react'

import { Link } from '@/lib/router'
import { useMe } from '@/features/auth/api'
import { navPermissionCheck } from '@/components/layout/navVisibility'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { bucketLabel, dateTime, dpd, inr, num, relativeTime } from '@/lib/format'

import {
  useFindings,
  useResolveFinding,
  useRunReconciliationNow,
  type FindingKind,
  type FindingStatus,
  type ReconciliationFinding,
  type ResolveAction,
} from './api'
import { LmsPostingTab } from './LmsPostingTab'

const STATUS_CHIPS: { key: FindingStatus | 'ALL'; label: string }[] = [
  { key: 'OPEN',          label: 'Open' },
  { key: 'INVESTIGATING', label: 'Investigating' },
  { key: 'ACCEPTED',      label: 'Accepted' },
  { key: 'REJECTED',      label: 'Rejected' },
  { key: 'ALL',           label: 'All' },
]

const KIND_CHIPS: { key: FindingKind | 'ALL'; label: string }[] = [
  { key: 'ALL',                     label: 'All kinds' },
  { key: 'EXTERNALLY_PAID',         label: 'Externally paid' },
  { key: 'PARTIAL_RECOVERY',        label: 'Partial recovery' },
  { key: 'BUCKET_MOVED',            label: 'Bucket moved' },
  { key: 'REVERSAL_SUSPECTED',      label: 'Reversal suspected' },
  { key: 'DUES_GREW_UNEXPECTEDLY',  label: 'Dues grew' },
  { key: 'DPD_WITHOUT_EMI_DUE',     label: 'DPD, no EMI due' },
]

const TABS = [
  { key: 'findings', label: 'Drift findings', permission: 'RECON_FINDING_VIEW' },
  { key: 'lms', label: 'LMS posting', permission: 'COLLECTION_RECONCILE' },
] as const

type TabKey = (typeof TABS)[number]['key']

/**
 * Reconciliation: drift findings from the dues files, and LMS posting — the
 * repayments export uploaded back once it is in the LMS. Each tab shows only
 * to holders of the permission its endpoints enforce; the first one held
 * opens, so the dashboard's links still land on the findings.
 */
export function ReconciliationPage() {
  const { data: me } = useMe()
  const can = navPermissionCheck(me)
  const tabs = TABS.filter((t) => can(t.permission))
  const [chosen, setChosen] = useState<TabKey | null>(null)
  const tab = chosen ?? tabs[0]?.key ?? null

  return (
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Operations</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy-900">Reconciliation</h1>
      </div>
      {tabs.length > 1 && (
        <div role="tablist" className="flex gap-1 border-b border-slate-200">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setChosen(t.key)}
              className={
                tab === t.key
                  ? '-mb-px border-b-2 border-navy-700 px-3 py-2 text-sm font-medium text-navy-900'
                  : '-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground hover:text-navy-900'
              }
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      {tab === 'findings' && <FindingsTab />}
      {tab === 'lms' && <LmsPostingTab />}
    </div>
  )
}

/** The drift findings queue — the "Drift findings" tab of {@link ReconciliationPage}. */
export function FindingsTab() {
  const [statusFilter, setStatusFilter] = useState<FindingStatus | 'ALL'>('OPEN')
  const [kindFilter, setKindFilter] = useState<FindingKind | 'ALL'>('ALL')
  const [q, setQ] = useState('')

  const { data, isPending, isError } = useFindings({
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    kind: kindFilter === 'ALL' ? undefined : kindFilter,
  })

  const filtered = useMemo(() => {
    const rows = data ?? []
    const norm = q.trim().toLowerCase()
    if (!norm) return rows
    return rows.filter((f) =>
      (f.loanAccountNo ?? '').toLowerCase().includes(norm) ||
      (f.customerName ?? '').toLowerCase().includes(norm) ||
      f.kind.toLowerCase().includes(norm),
    )
  }, [data, q])

  // KPI strip uses the unfiltered list so the counts represent the queue, not search.
  const openCount = (data ?? []).filter((f) => f.status === 'OPEN').length
  const investCount = (data ?? []).filter((f) => f.status === 'INVESTIGATING').length
  const high = (data ?? []).filter((f) => f.confidence >= 80).length
  const acceptedToday = (data ?? []).filter(
    (f) => f.status === 'ACCEPTED' && f.resolvedAt &&
      Date.parse(f.resolvedAt) > Date.now() - 24 * 60 * 60 * 1000,
  ).length

  const runNow = useRunReconciliationNow()
  const { data: me } = useMe()
  const canUpload = navPermissionCheck(me)('INGESTION_UPLOAD')
  const [runResult, setRunResult] = useState<string | null>(null)

  function handleRunNow() {
    setRunResult(null)
    runNow.mutate(undefined, {
      onSuccess: (r) => {
        setRunResult(
          `Reconciled batch ${r.batchId.slice(0, 8)} — opened ${r.opened}, updated ${r.updated}, cured ${r.cured}, skipped ${r.skipped}.`,
        )
      },
      onError: (err) => {
        if (isAxiosError(err)) {
          const body = err.response?.data
          setRunResult(typeof body === 'string' ? body : (body?.message ?? 'Run failed.'))
        } else {
          setRunResult('Run failed.')
        }
      },
    })
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Drift signals raised by the reconciliation engine. Accept applies the suggested action,
            reject marks the row as a false positive, investigate parks it for off-system follow-up.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Button
            onClick={handleRunNow}
            disabled={runNow.isPending}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${runNow.isPending ? 'animate-spin' : ''}`} />
            {runNow.isPending ? 'Running…' : 'Run reconciliation now'}
          </Button>
          {runResult && (
            <span className="text-2xs text-navy-700">{runResult}</span>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Open" value={num(openCount)} hint="Awaiting review" loading={isPending} deltaSense="lower-is-better" />
        <Kpi label="Investigating" value={num(investCount)} hint="Parked by ops" loading={isPending} deltaSense="neutral" />
        <Kpi label="High confidence" value={num(high)} hint="≥ 80% — auto-accept candidates" loading={isPending} deltaSense="higher-is-better" />
        <Kpi label="Accepted today" value={num(acceptedToday)} hint="Last 24h" loading={isPending} deltaSense="higher-is-better" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Findings queue</CardTitle>
              <CardDescription>Auto-refreshes every 10s.</CardDescription>
            </div>
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search loan, customer or kind…"
                style={{ paddingLeft: '2rem' }}
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-2xs uppercase tracking-wider text-navy-600">Status</span>
            <ChipGroup>
              {STATUS_CHIPS.map((s) => (
                <Chip key={s.key} active={statusFilter === s.key} onClick={() => setStatusFilter(s.key)}>
                  {s.label}
                </Chip>
              ))}
            </ChipGroup>
            <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            <span className="text-2xs uppercase tracking-wider text-navy-600">Kind</span>
            <ChipGroup>
              {KIND_CHIPS.map((k) => (
                <Chip key={k.key} active={kindFilter === k.key} onClick={() => setKindFilter(k.key)}>
                  {k.label}
                </Chip>
              ))}
            </ChipGroup>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {isPending && <SkeletonRows rows={5} cols={1} />}
          {isError && (
            <div className="px-5 pb-4 text-sm text-negative">
              Couldn't load findings. You may not have RECON_FINDING_VIEW permission.
            </div>
          )}
          {!isPending && filtered.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">No findings match this filter</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                Upload a fresh{' '}
                {canUpload ? (
                  <Link to="/ingest" className="text-navy-700 underline-offset-4 hover:underline">DPD_DUES</Link>
                ) : (
                  'DPD_DUES'
                )}{' '}
                batch where outstanding moves; the engine emits findings as it reconciles.
              </p>
            </div>
          )}
          {filtered.length > 0 && (
            <ul className="divide-y divide-slate-200">
              {filtered.map((f) => <FindingItem key={f.findingId} f={f} />)}
            </ul>
          )}
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            Showing {num(filtered.length)} of {num(data?.length ?? 0)}
          </span>
          <span className="inline-flex items-center gap-2 text-2xs uppercase tracking-wider text-muted-foreground">
            <RefreshCw className="h-3 w-3" /> 10s
          </span>
        </CardFooter>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// One finding — collapsed by default; expand to see evidence + actions.
// ---------------------------------------------------------------------------

function FindingItem({ f }: { f: ReconciliationFinding }) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const resolve = useResolveFinding()

  function act(action: ResolveAction) {
    resolve.mutate({ findingId: f.findingId, body: { action, note: note.trim() || null } })
  }

  const errorMessage = resolve.isError
    ? isAxiosError(resolve.error) && resolve.error.response?.status === 403
      ? 'You do not have RECON_FINDING_RESOLVE permission.'
      : isAxiosError(resolve.error) && resolve.error.response?.data?.message
      ? resolve.error.response.data.message
      : 'Resolution failed.'
    : null

  return (
    <li className="px-5 py-3">
      <div className="flex items-start gap-3">
        <KindGlyph kind={f.kind} confidence={f.confidence} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <KindBadge kind={f.kind} />
            <ConfidenceBadge confidence={f.confidence} />
            <StatusBadge status={f.status} />
            {f.suggestedAction !== 'NO_OP' && <SuggestedActionBadge action={f.suggestedAction} />}
            <span className="text-2xs uppercase tracking-wider text-muted-foreground" title={dateTime(f.detectedAt)}>
              {relativeTime(f.detectedAt)}
            </span>
          </div>

          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 text-sm">
            {f.loanAccountNo && (
              <Link
                to="/cases/$caseId"
                params={{ caseId: f.caseId ?? '' }}
                className="font-mono text-xs text-navy-900 underline-offset-4 hover:underline"
              >
                {f.loanAccountNo}
              </Link>
            )}
            {f.customerName && <span className="text-navy-900">{f.customerName}</span>}
            {f.currentBucket && (
              <span className="text-xs text-muted-foreground">
                <Badge tone="slate">{bucketLabel(f.currentBucket)}</Badge>
                <span className="ml-2 tabular-nums">DPD {dpd(f.currentDpd)}</span>
                <span className="ml-2 tabular-nums">{inr(f.currentOutstanding)} outstanding</span>
              </span>
            )}
          </div>

          {open && (
            <div className="mt-3 space-y-3 animate-fade-in">
              <EvidenceCard evidenceJson={f.evidenceJson} />
              {f.status === 'OPEN' && (
                <div className="space-y-2">
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Resolution note (optional, 1024 chars)…"
                    maxLength={1024}
                  />
                  {errorMessage && (
                    <Alert variant="destructive">
                      <AlertTitle>Could not resolve</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <Button onClick={() => act('ACCEPT')} disabled={resolve.isPending}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Accept · {suggestionLabel(f.suggestedAction)}
                    </Button>
                    <Button variant="outline" onClick={() => act('INVESTIGATE')} disabled={resolve.isPending}>
                      <Eye className="h-3.5 w-3.5" />
                      Investigate
                    </Button>
                    <Button variant="ghost" onClick={() => act('REJECT')} disabled={resolve.isPending}>
                      <XCircle className="h-3.5 w-3.5" />
                      Reject
                    </Button>
                  </div>
                </div>
              )}
              {f.status !== 'OPEN' && f.resolutionNote && (
                <p className="rounded-md border border-slate-200 bg-navy-50/40 p-3 text-xs text-navy-800">
                  <span className="font-semibold uppercase tracking-wider text-navy-600">Note · </span>
                  {f.resolutionNote}
                </p>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-2xs font-semibold uppercase tracking-wider text-navy-700 hover:underline"
        >
          {open ? 'Hide' : 'Review'} <ArrowRight className="ml-0.5 inline-block h-3 w-3" />
        </button>
      </div>
    </li>
  )
}

function EvidenceCard({ evidenceJson }: { evidenceJson: string }) {
  let parsed: Record<string, unknown> = {}
  try {
    parsed = JSON.parse(evidenceJson)
  } catch {
    return null
  }
  const entries = Object.entries(parsed)
  if (entries.length === 0) return null
  return (
    <div className="rounded-md border border-slate-200 bg-navy-50/40 p-3">
      <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">Evidence</p>
      <dl className="mt-1 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-[10rem_1fr]">
        {entries.map(([k, v]) => (
          <DLPair key={k} label={k} value={String(v ?? '—')} />
        ))}
      </dl>
    </div>
  )
}

function DLPair({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-2xs uppercase tracking-wider text-muted-foreground">{label.replace(/_/g, ' ')}</dt>
      <dd className="font-mono tabular-nums text-navy-900">{value}</dd>
    </>
  )
}

// ---------------------------------------------------------------------------
// Visual atoms
// ---------------------------------------------------------------------------

function KindGlyph({ kind, confidence }: { kind: FindingKind; confidence: number }) {
  // Red-on-NEGATIVE only — REVERSAL / DUES_GREW are negative; everything else
  // is informational.
  const negative = kind === 'REVERSAL_SUSPECTED' || kind === 'DUES_GREW_UNEXPECTEDLY'
  const positive = kind === 'EXTERNALLY_PAID'
  const cls =
    negative ? 'bg-negative-soft text-negative'
    : positive ? 'bg-positive-soft text-positive'
    : confidence >= 80 ? 'bg-warning-soft text-warning'
    : 'bg-info-soft text-info'
  return (
    <span className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${cls}`}>
      <AlertTriangle className="h-3.5 w-3.5" />
    </span>
  )
}

function KindBadge({ kind }: { kind: FindingKind }) {
  const labels: Record<FindingKind, string> = {
    EXTERNALLY_PAID:        'Externally paid',
    PARTIAL_RECOVERY:       'Partial recovery',
    BUCKET_MOVED:           'Bucket moved',
    REVERSAL_SUSPECTED:     'Reversal',
    DUES_GREW_UNEXPECTEDLY: 'Dues grew',
    DPD_WITHOUT_EMI_DUE:    'DPD, no EMI due',
  }
  const tone =
    kind === 'EXTERNALLY_PAID'        ? 'positive'
    : kind === 'REVERSAL_SUSPECTED'   ? 'negative'
    : kind === 'DUES_GREW_UNEXPECTEDLY' ? 'negative'
    : 'warning'
  return <Badge tone={tone as 'positive' | 'negative' | 'warning'}>{labels[kind]}</Badge>
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const tone = confidence >= 80 ? 'navy' : confidence >= 60 ? 'info' : 'slate'
  return <Badge tone={tone as 'navy' | 'info' | 'slate'}>{confidence}% confident</Badge>
}

function StatusBadge({ status }: { status: FindingStatus }) {
  const map: Record<FindingStatus, 'info' | 'positive' | 'negative' | 'warning'> = {
    OPEN:          'info',
    ACCEPTED:      'positive',
    REJECTED:      'negative',
    INVESTIGATING: 'warning',
  }
  return <Badge tone={map[status]}>{status}</Badge>
}

function SuggestedActionBadge({ action }: { action: 'CLOSE_CASE' | 'ADJUST_OUTSTANDING' | 'OPEN_INVESTIGATION' | 'NO_OP' }) {
  return (
    <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-2xs uppercase tracking-wider text-navy-700">
      Suggest · {action.replace(/_/g, ' ')}
    </span>
  )
}

function suggestionLabel(action: string): string {
  switch (action) {
    case 'CLOSE_CASE':         return 'close the case'
    case 'ADJUST_OUTSTANDING': return 'adjust outstanding'
    case 'OPEN_INVESTIGATION': return 'open investigation'
    default:                   return 'no-op'
  }
}
